import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, ChevronRight, Download, Eye, Loader2, QrCode } from 'lucide-react';
import { Card } from '../../../../../../../components/ui/card';
import { Button } from '../../../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../../../components/ui/dialog';
import type { SpoolCard, WeldJoint } from '../../SpoolsProduction';
import type { SignaturePanelProps } from './signatureTypes';
import { WeldTelemetryModal } from './WeldTelemetryModal';
import { weldJointStatusCell } from './weldStatusBadge';
import {
  getLastDedicatedRepairWeldingInfo,
  getWeldStepPanelPayload,
} from './weldStepPanelHydration';
import { generateQrCodeSpoolsbase } from '../../../../../../../redux/Spoolsbase/saga';
import { Notification } from '../../../../../../../components/ui/custom-notification';

/** Synthetic row when the pipe/spool declares `welds` but API has not returned weld_joint rows yet. */
export function placeholderWeldJoint(slotIndex: number): WeldJoint {
  const n = slotIndex + 1;
  return {
    id: `local-wt-${n}`,
    number: `WT-${n}`,
    type: '',
    welder: '',
    status: 'pending',
  };
}

/**
 * Rows for the weld table: real `weldJoints` from the API where present, padded with `WT-1…` pending
 * placeholders up to `spool.welds` (and never fewer than existing joint rows).
 */
export function buildDisplayWeldJoints(spool: SpoolCard): WeldJoint[] {
  const joints = spool.weldJoints ?? [];
  const declared = Math.max(0, Math.floor(Number(spool.welds) || 0));
  const target = Math.max(declared, joints.length);
  if (target === 0) return [];
  return Array.from({ length: target }, (_, i) => joints[i] ?? placeholderWeldJoint(i));
}

function payloadField(payload: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!payload) return undefined;
  for (const key of keys) {
    if (payload[key] != null) return payload[key];
  }
  return undefined;
}

function parseIds(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((item) => String(item)).filter(Boolean);
  const value = String(raw).trim();
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map((item) => String(item)).filter(Boolean);
  } catch {
    // Use comma-separated or single value below.
  }
  return value.split(',').map((item) => item.trim()).filter(Boolean);
}

function resolveWelderDisplay(raw: unknown, sig: SignaturePanelProps): string | undefined {
  const ids = parseIds(raw);
  if (ids.length === 0) return undefined;
  const labels = ids.map((id) => sig.welderList.find((welder) => String(welder.value) === id)?.label ?? id);
  return labels.join(', ');
}

function getDisplayPayload(weld: WeldJoint): Record<string, unknown> | null {
  return getLastDedicatedRepairWeldingInfo(weld)?.payload ?? getWeldStepPanelPayload(weld, 'welding');
}

function resolveWeldDisplay(weld: WeldJoint, sig: SignaturePanelProps) {
  const payload = getDisplayPayload(weld);
  const number = payloadField(payload, 'weldNumberInput', 'weld_number_input');
  const type = payloadField(payload, 'weldType', 'weld_type');
  const welderIds = payloadField(payload, 'welderIds', 'welder_ids', 'productionWeldId', 'production_weld_id');

  return {
    number: number != null && String(number).trim() ? String(number).trim() : weld.number,
    type: type != null && String(type).trim() ? String(type).trim() : weld.type,
    welder: resolveWelderDisplay(welderIds, sig) ?? weld.welder,
  };
}

export type WeldTableProps = {
  spool: SpoolCard;
  sig: SignaturePanelProps;
  /** Controlled selection (e.g. from SpoolDetailModal). Omit for internal-only state (WeldingPanel). */
  selectedWeldId?: string | null;
  onSelectedWeldChange?: (id: string | null) => void;
  /** When set (e.g. SpoolDetailModal), "Continue to weld steps" leaves the weld record and opens the workflow step strip. */
  onContinueToWorkflow?: () => void;
};

/** Weld register, full-screen weld detail modal, and traceability matrix for the welding step. */
export function WeldTable({ spool, sig, selectedWeldId, onSelectedWeldChange, onContinueToWorkflow }: WeldTableProps) {
  const [internalId, setInternalId] = useState<string | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [telemetryModalOpen, setTelemetryModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [weldQrCodes, setWeldQrCodes] = useState<Array<{ text: string; image: string }>>([]);
  const isControlled = typeof onSelectedWeldChange === 'function';
  const selectedId = isControlled ? (selectedWeldId ?? null) : internalId;
  const setSelectedId = isControlled ? onSelectedWeldChange! : setInternalId;

  const displayWeldJoints = useMemo(() => buildDisplayWeldJoints(spool), [spool]);

  const activeWeld = displayWeldJoints.find((w) => w.id === selectedId) ?? null;

  useEffect(() => {
    if (!activeWeld) setDetailModalOpen(false);
  }, [activeWeld]);

  useEffect(() => {
    if (!activeWeld || activeWeld.status === 'pending') setTelemetryModalOpen(false);
  }, [activeWeld]);

  const closeDetailModal = () => setDetailModalOpen(false);

  const openWeldTelemetry = (weldId: string) => {
    const w = displayWeldJoints.find((joint) => joint.id === weldId);
    if (!w || w.status === 'pending') return;
    setSelectedId(weldId);
    setDetailModalOpen(false);
    setTelemetryModalOpen(true);
  };

  const showWeldTelemetryForActive = activeWeld != null && activeWeld.status !== 'pending';

  const handleGenerateWeldQrCodes = async () => {
    const weldIds = displayWeldJoints
      .map((weld) => resolveWeldDisplay(weld, sig).number)
      .map((weldId) => weldId.trim())
      .filter(Boolean);

    if (weldIds.length === 0) {
      Notification.warning('Weld QR Code', 'No weld IDs are available to generate QR codes.');
      return;
    }

    setQrLoading(true);
    try {
      const qrCodes = await generateQrCodeSpoolsbase(weldIds);
      const validQrCodes = (qrCodes ?? []).filter(
        (qrCode): qrCode is { text: string; image: string } =>
          Boolean(qrCode?.text && qrCode?.image)
      );
      if (validQrCodes.length === 0) {
        Notification.error('Weld QR Code', 'QR code generation failed. Please try again.');
        return;
      }
      setWeldQrCodes(validQrCodes);
      setQrModalOpen(true);
    } finally {
      setQrLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card className="border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800">Active Spool — {spool.name}</h3>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {onContinueToWorkflow ? 'Click a row to open weld workflow' : 'Click a row to open the full weld record'}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={qrLoading || displayWeldJoints.length === 0}
              onClick={handleGenerateWeldQrCodes}
            >
              {qrLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              Generate QR Code
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2 text-left font-medium">Weld</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Welder</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="w-12 px-2 py-2 text-center font-medium" title="Weld data">
                  <Eye className="mx-auto h-3.5 w-3.5 text-gray-400" aria-hidden />
                </th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayWeldJoints.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                    No welds configured for this spool.
                  </td>
                </tr>
              ) : null}
              {displayWeldJoints.map((w) => {
                const display = resolveWeldDisplay(w, sig);
                return (
                <tr
                  key={w.id}
                  onClick={() => {
                    if (onContinueToWorkflow) {
                      setSelectedId(w.id);
                      setDetailModalOpen(false);
                      onContinueToWorkflow();
                      return;
                    }
                    if (selectedId === w.id) {
                      setDetailModalOpen((open) => !open);
                    } else {
                      setSelectedId(w.id);
                      setDetailModalOpen(true);
                    }
                  }}
                  className={`cursor-pointer hover:bg-gray-50 transition-colors ${selectedId === w.id ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-3 py-2 font-mono font-semibold text-gray-800">{display.number}</td>
                  <td className="px-3 py-2 text-gray-600">{display.type?.trim() ? display.type : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{display.welder?.trim() ? display.welder : '—'}</td>
                  <td className="px-3 py-2 align-top">{weldJointStatusCell(w)}</td>
                  <td className="px-2 py-2 text-center">
                    {w.status !== 'pending' ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-500 hover:text-gray-800"
                        aria-label={`Weld data for ${display.number}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openWeldTelemetry(w.id);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-400">
                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${selectedId === w.id ? 'rotate-90' : ''}`} />
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border border-gray-200">
        <div className="border-b bg-gray-50 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">Traceability Matrix</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50 uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 text-left font-medium">Weld</th>
                <th className="px-3 py-2 text-left font-medium">Side A Heat</th>
                <th className="px-3 py-2 text-left font-medium">Side B Heat</th>
                <th className="px-3 py-2 text-left font-medium">WPS</th>
                <th className="px-3 py-2 text-left font-medium">Consumable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-mono">
              {displayWeldJoints
                .filter((w) => w.sideAHeat)
                .map((w) => {
                  const display = resolveWeldDisplay(w, sig);
                  return (
                  <tr key={w.id} className="text-gray-700">
                    <td className="px-3 py-1.5">{display.number}</td>
                    <td className="px-3 py-1.5 text-gray-500">{w.sideAHeat}</td>
                    <td className="px-3 py-1.5 text-gray-500">{w.sideBHeat}</td>
                    <td className="px-3 py-1.5 text-gray-500">{w.wps}</td>
                    <td className="px-3 py-1.5 text-gray-500">{w.consumableLot}</td>
                  </tr>
                );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog
        open={detailModalOpen && !!activeWeld}
        onOpenChange={(o) => {
          if (!o) closeDetailModal();
        }}
      >
        <DialogContent className="m-0 h-screen max-h-none w-screen max-w-none gap-0 overflow-hidden rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
          <div className="flex h-screen flex-col">
            <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  onClick={closeDetailModal}
                  aria-label="Back to weld list"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </Button>
                <div className="min-w-0 space-y-1">
                  <DialogTitle className="text-left text-lg font-bold text-gray-900">Weld record</DialogTitle>
                 
                  {activeWeld && (() => {
                    const display = resolveWeldDisplay(activeWeld, sig);
                    return (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
                       <p className="truncate text-sm text-gray-500">
                    <span className="font-medium text-gray-700">Spool:</span> {spool.name}
                  </p>
                      <span>
                        <span className="text-gray-500">Weld:</span>{' '}
                        <span className="font-mono font-semibold text-gray-900">{display.number}</span>
                      </span>
                      <span className="hidden text-gray-300 sm:inline">|</span>
                      <span>
                        <span className="text-gray-500">Type:</span> {display.type?.trim() ? display.type : '—'}
                      </span>
                      <span className="hidden text-gray-300 sm:inline">|</span>
                      <span>
                        <span className="text-gray-500">Welder:</span> {display.welder?.trim() ? display.welder : '—'}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-gray-500">Status:</span>
                        {weldJointStatusCell(activeWeld)}
                      </span>
                    </div>
                  );
                  })()}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 pt-0.5">
                {showWeldTelemetryForActive ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      if (activeWeld) openWeldTelemetry(activeWeld.id);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    Weld data
                  </Button>
                ) : null}
                {onContinueToWorkflow ? (
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#e04530] hover:bg-[#e04530]/90 text-white"
                    onClick={() => {
                      closeDetailModal();
                      onContinueToWorkflow();
                    }}
                  >
                    Continue to weld steps
                  </Button>
                ) : null}
              </div>
            </DialogHeader>
            {/* <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
              {activeWeld && <WeldDetailPanel weld={activeWeld} sig={sig} />}
            </div> */}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <div className="max-h-[calc(85vh-2rem)] overflow-y-auto p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-blueprint-blue">
                Weld QR Codes - {spool.name}
              </h2>
            </div>
            {weldQrCodes.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {weldQrCodes.map((qrCode, index) => (
                  <div key={`${qrCode.text}-${index}`} className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-full rounded-lg border border-[#E2E5EA] bg-white p-4 shadow-md">
                      <div className="mb-2 text-center">
                        <label className="text-sm font-medium text-text-grey">
                          Weld ID: {qrCode.text}
                        </label>
                      </div>
                      <img
                        src={qrCode.image}
                        alt={`Weld ID ${qrCode.text} QR code`}
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      className="mx-auto w-1/2 gap-1.5"
                      onClick={() => {
                        const safeWeldId = qrCode.text.replace(/[\\/:*?"<>|]/g, '-');
                        const link = document.createElement('a');
                        link.href = qrCode.image;
                        link.download = `weld-${safeWeldId}-qr-code.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        Notification.success('Download Started', `QR code for ${qrCode.text} is being downloaded...`);
                      }}
                    >
                      <Download className="h-4 w-4" />
                      Download Weld {index + 1}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-heritage-grey">No QR codes available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <WeldTelemetryModal
        open={telemetryModalOpen && showWeldTelemetryForActive}
        onClose={() => setTelemetryModalOpen(false)}
        spool={spool}
        weld={activeWeld}
      />
    </div>
  );
}
